import type { TreatmentCatalogItem } from '../types/treatmentCatalog.types';
import { getCategoryId, getCategoryName } from '../types/treatmentCatalog.types';

export type CatalogStatusFilter = 'all' | 'active' | 'inactive';

export interface FilterCatalogItemsOptions {
  searchTerm?: string;
  categoryFilter?: string;
  statusFilter?: CatalogStatusFilter;
  /** Shorthand for statusFilter: 'active'. Kept for pickers / plan editors. */
  activeOnly?: boolean;
}

export interface CatalogCategoryOption {
  id: string;
  name: string;
}

export const filterCatalogItems = (
  items: TreatmentCatalogItem[],
  options: FilterCatalogItemsOptions = {}
): TreatmentCatalogItem[] => {
  const {
    searchTerm = '',
    categoryFilter = 'all',
    statusFilter = 'all',
    activeOnly = false,
  } = options;

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const resolvedStatus: CatalogStatusFilter = activeOnly ? 'active' : statusFilter;

  return items.filter((item) => {
    if (resolvedStatus === 'active' && !item.isActive) {
      return false;
    }
    if (resolvedStatus === 'inactive' && item.isActive) {
      return false;
    }

    const categoryName = getCategoryName(item.category).toLowerCase();

    const matchesSearch =
      normalizedSearch.length === 0 ||
      item.name.toLowerCase().includes(normalizedSearch) ||
      item.code.toLowerCase().includes(normalizedSearch) ||
      categoryName.includes(normalizedSearch) ||
      (item.description?.toLowerCase().includes(normalizedSearch) ?? false);

    const categoryId = getCategoryId(item.category);
    const matchesCategory =
      categoryFilter === 'all' ||
      (categoryFilter === 'unassigned' ? !categoryId : categoryId === categoryFilter);

    return matchesSearch && matchesCategory;
  });
};

export const getCatalogCategories = (items: TreatmentCatalogItem[]): CatalogCategoryOption[] => {
  const map = new Map<string, string>();
  for (const item of items) {
    const id = getCategoryId(item.category);
    if (!id) continue;
    map.set(id, getCategoryName(item.category));
  }
  return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
};
